import type { ReactNode } from 'react';
import type { SerializedBoardDto } from '../../api/puzzles';
import type { MovePlayed } from '../../game/store';
import { Board } from '../board/Board';

interface EndOfLifeModalProps {
  won: boolean;
  score: number;
  target: number;
  lifeNumber: number;
  livesLeft: number;
  /** Final board of the life — shown as a read-only snapshot. */
  board?: SerializedBoardDto;
  /** Words played this life with their scores. */
  moves?: MovePlayed[];
  onTryAgain: () => void;
  onShare: () => void;
  onBackToToday: () => void;
  onSeeSolution?: () => void;
  /** Extra content, e.g. the guest sign-in nudge. */
  children?: ReactNode;
}

export function EndOfLifeModal({
  won,
  score,
  target,
  lifeNumber,
  livesLeft,
  board,
  moves,
  onTryAgain,
  onShare,
  onBackToToday,
  onSeeSolution,
  children,
}: EndOfLifeModalProps) {
  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label={won ? 'You won' : 'Life lost'}
      className="fixed inset-0 z-40 flex items-center justify-center bg-black/40 p-4"
    >
      <div className="max-h-[90vh] w-full max-w-sm overflow-y-auto rounded-xl bg-white p-6 text-center shadow-xl">
        {won ? (
          <>
            <p className="text-4xl" aria-hidden="true">
              🎉
            </p>
            <h2 className="mt-2 text-xl font-bold text-emerald-700">You beat the target!</h2>
            <p className="mt-1 text-stone-600">
              {score}/{target} on life {lifeNumber}.
            </p>
          </>
        ) : (
          <>
            <p className="text-4xl" aria-hidden="true">
              😮‍💨
            </p>
            <h2 className="mt-2 text-xl font-bold">
              {score}/{target}
            </h2>
            <p className="mt-1 text-stone-600">
              {livesLeft > 0
                ? `${livesLeft} ${livesLeft === 1 ? 'life' : 'lives'} left. Try again?`
                : 'No lives left today.'}
            </p>
          </>
        )}

        {board && board.placed.length > 0 ? (
          <div className="mx-auto mt-4 max-w-64" data-testid="result-board">
            <Board board={board} staged={[]} testIdPrefix="result-" hidePremiumLabels />
          </div>
        ) : null}

        {moves && moves.length > 0 ? (
          <p data-testid="result-words" className="mt-3 text-sm text-stone-700">
            {moves.map((m, i) => (
              <span key={`${m.word}-${i}`}>
                {i > 0 ? ' · ' : ''}
                <span className="font-semibold">{m.word}</span> {m.score}
              </span>
            ))}
          </p>
        ) : null}

        {children}

        <div className="mt-5 space-y-2">
          {won || livesLeft === 0 ? (
            <button
              type="button"
              onClick={onShare}
              className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800"
            >
              Share
            </button>
          ) : null}
          {livesLeft > 0 ? (
            <button
              type="button"
              onClick={onTryAgain}
              className="w-full rounded-md bg-emerald-700 px-4 py-2.5 font-medium text-white hover:bg-emerald-800"
            >
              {won ? 'Try for a higher score' : 'Try again'}
            </button>
          ) : null}
          {onSeeSolution ? (
            <button
              type="button"
              onClick={onSeeSolution}
              className="w-full rounded-md border border-emerald-700 px-4 py-2.5 font-medium text-emerald-800 hover:bg-emerald-50"
            >
              See what was possible
            </button>
          ) : null}
          <button
            type="button"
            onClick={onBackToToday}
            className="w-full rounded-md border border-stone-300 px-4 py-2.5 font-medium text-stone-700 hover:bg-stone-50"
          >
            Back to today
          </button>
        </div>
      </div>
    </div>
  );
}
